// Channel connect page (the target of the "Connect Telegram" email button).
//
// The email deliberately carries no code — this page mints the single-use link
// on arrival and bounces straight into the messenger, so a forwarded mail links
// nobody and the durable outbox never holds a usable credential.
//
// Typical path: member reads Chess on a desktop, mails themselves the button,
// taps it on the phone that has Telegram, presses Start. Done.

import { api, ApiError } from "../api.ts";
import { esc, h } from "../dom.ts";
import { t } from "../i18n/index.ts";
import { navigate, type Params } from "../router.ts";
import { currentUser } from "../session.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";

const LINKABLE = new Set(["telegram", "whatsapp", "imessage"]);

export async function connectScreen(params: Params): Promise<HTMLElement> {
  const provider = (params.provider ?? "").toLowerCase();
  if (!currentUser()) {
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }
  if (!LINKABLE.has(provider)) {
    return notice(t("connect.unknownChannel"));
  }

  const label = providerLabel(provider);
  const el = h(`
    <main class="card connect-card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="msg" role="status" aria-live="polite">${t("connect.preparing", { channel: label })}</p>
      <div class="connect-action"></div>
    </main>`);
  const msg = el.querySelector<HTMLElement>(".msg")!;
  const action = el.querySelector<HTMLElement>(".connect-action")!;

  let issued: Awaited<ReturnType<typeof api.requestGatewayLink>>;
  try {
    issued = await api.requestGatewayLink(provider);
  } catch (error) {
    msg.className = "msg bad";
    msg.textContent =
      error instanceof ApiError ? error.message : t("connect.failed");
    return el;
  }

  if (!issued.deep_link) {
    // No bot username to build a deep link from — fall back to the code.
    msg.textContent = t("connect.manual", { channel: label });
    action.innerHTML = `<strong class="gateway-code">${esc(`/link ${issued.code}`)}</strong>`;
    return el;
  }

  const target = issued.deep_link;
  msg.textContent = t("connect.opening", { channel: label });
  action.innerHTML = `
    <a class="btn primary connect-open" href="${esc(target)}">${t("connect.open", {
      channel: label,
    })}</a>
    <p class="fine">${t("connect.pressStart", { channel: label })}</p>`;
  // Hand off automatically — the member already expressed intent by opening
  // this page, and a browser that blocks the redirect still shows the button.
  window.setTimeout(() => {
    if (el.isConnected) window.location.assign(target);
  }, 600);
  return el;
}

function providerLabel(provider: string): string {
  if (provider === "telegram") return "Telegram";
  if (provider === "whatsapp") return "WhatsApp";
  return "Apple Messages";
}

function notice(text: string): HTMLElement {
  return h(`
    <main class="card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="msg bad">${esc(text)}</p>
    </main>`);
}
